import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Brain, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const DIFF = { easy: "text-[#00E676]", medium: "text-[#FFC857]", hard: "text-[#FF5C5C]" };

export default function CancelStrategy({ leak, onStrategy }) {
  const [s, setS] = useState(leak.cancel_strategy || null);
  const [loading, setLoading] = useState(!leak.cancel_strategy);

  useEffect(() => {
    if (s) return;
    let stale = false;
    api.get(`/leaks/${leak.id}/cancel-strategy`)
      .then(({ data }) => { if (stale) return; setS(data); onStrategy?.(data); })
      .catch(() => { if (!stale) toast.error("Couldn't build a strategy — using default steps"); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [leak.id, s, onStrategy]);

  const copyScript = async () => {
    const txt = (s.retention_script || []).map((r) => `They: ${r.they_say}\nYou: ${r.you_say}`).join("\n\n");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Script copied");
    } catch {
      toast.error("Clipboard blocked — select the script text to copy it");
    }
  };

  if (loading) {
    return (
      <div className="mt-2 p-4 rounded-md bg-[#0C0D0E] border border-[#282C30] animate-pulse space-y-2" data-testid="cancel-strategy-loading">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#00E676]"><Brain className="h-3.5 w-3.5" /> Claude is picking the best cancel path…</div>
        <div className="h-4 w-2/3 rounded bg-[#1E2226]" /><div className="h-4 w-1/2 rounded bg-[#1E2226]" />
      </div>
    );
  }
  if (!s) return null;

  return (
    <div className="mt-2 space-y-3" data-testid="cancel-strategy">
      <div className="p-4 rounded-md bg-[#0C0D0E] border border-[#282C30]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#00E676]"><Brain className="h-3.5 w-3.5" /> BEST PATH</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#606872]">
            <span className={DIFF[s.difficulty] || ""} data-testid="cancel-strategy-difficulty">{s.difficulty}</span> · ~{s.est_minutes} min
          </div>
        </div>
        <div className="text-sm font-semibold text-[#F4F1EA] mt-1" data-testid="cancel-strategy-path">{s.path_label}</div>
        <div className="text-xs text-[#A0A7B0] mt-0.5">{s.why}</div>
        <div className="space-y-1.5 mt-3" data-testid="cancel-strategy-steps">
          {(s.steps || []).map((st, i) => (
            <div key={st} className="text-sm text-[#A0A7B0] flex gap-2">
              <span className="font-mono text-[10px] mt-1 text-[#606872]">{String(i + 1).padStart(2, "0")}</span><span>{st}</span>
            </div>
          ))}
        </div>
      </div>

      {s.retention_script?.length > 0 && (
        <div className="p-4 rounded-md bg-[#0C0D0E] border border-[#282C30]" data-testid="cancel-strategy-script">
          <div className="flex items-center justify-between">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">RETENTION SCRIPT · WHEN THEY PUSH BACK</div>
            <button onClick={copyScript} className="text-[#A0A7B0] hover:text-[#F4F1EA] flex items-center gap-1 text-[11px]" data-testid="cancel-strategy-copy-script"><Copy className="h-3 w-3" /> Copy</button>
          </div>
          <div className="mt-2 space-y-2">
            {s.retention_script.map((r) => (
              <div key={r.they_say} className="text-xs">
                <div className="text-[#606872] italic">“{r.they_say}”</div>
                <div className="text-[#F4F1EA] mt-0.5 pl-3 border-l border-[#00E676]/50">{r.you_say}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(s.gotchas?.length > 0 || s.refund_after_cancel) && (
        <div className="text-xs text-[#A0A7B0] space-y-1" data-testid="cancel-strategy-gotchas">
          {(s.gotchas || []).map((g) => (
            <div key={g} className="flex gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-[#FFC857] shrink-0 mt-0.5" /><span>{g}</span></div>
          ))}
          {s.refund_after_cancel && <div className="text-[#00E676]/90 pt-1">{s.refund_after_cancel}</div>}
        </div>
      )}
    </div>
  );
}
